import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { useConnection } from 'wagmi';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  BridgeToken,
  DEFAULT_BRIDGE_INPUT_DECIMALS,
} from 'structure/bridge/constants/bridgeTokens';
import { SelectToken } from 'structure/pv2/vault/SelectToken';
import {
  firstDecimals,
  formatBridgeBalanceAmount,
  formatBridgeTokenAmount,
  getMaximumBridgeAmount,
  getSupportedTokens,
} from 'structure/bridge/utils/bridgeStateHelpers';
import { DECIMALS_INTERNAL } from 'structure/bridge/constants/constants';
import { LabelSmall } from 'baseui/typography';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import styles from '../bridge.module.css';
import Image from 'next/image';
import { getStaticAssetUrl } from 'utils/url';
import { Input } from 'structure/staking/components/form/Input';
import { NetworkSelection } from 'structure/migration/components/MigrationPanel/NetworkSelection';
export function BridgeAmountInput({
  chainId,
  allowedNetworks,
  tokenBalance,
  destinationProxyLiquidity,
  isDestinationProxyLiquidityLoading = false,
}: {
  chainId: number;
  allowedNetworks: number[];
  tokenBalance: bigint;
  destinationProxyLiquidity?: bigint;
  isDestinationProxyLiquidityLoading?: boolean;
}) {
  const { t } = useTranslation('common');
  const { address: userAddress } = useConnection();
  const [formTokenAmount, setFormTokenAmount] = useState<string>('0');
  const [selectTokenOpen, setSelectTokenOpen] = useState(false);
  const [debouncedFormTokenAmount] = useDebounce(formTokenAmount, 666);
  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);

  // in order to only show tokens that exist on the current chain
  // we first filter the tokens and then set the default selected currency
  // filter out tokens that are not deployed on current chain
  const filteredTokens: BridgeToken[] = useMemo(
    () => getSupportedTokens(chainId),
    [chainId]
  );

  // set default selected token
  const [selectedCurrency, setSelectedCurrency] = useState(filteredTokens[0]);

  const assetOptions = useMemo(
    () =>
      filteredTokens.map((token) => ({
        symbol: token.symbol,
        name: token.symbol,
        decimals: token.decimals,
        address: token.address[chainId],
      })),
    [chainId, filteredTokens]
  );

  const selectedAsset = assetOptions.find(
    (asset) => asset.address === selectedCurrency.address[chainId]
  );

  // reset amount and token on changes
  useEffect(() => {
    setBridgeState((prevState) => ({
      ...prevState,
      token: filteredTokens[0],
      amount: 0,
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
      tokenQuoteLoading: false,
    }));
    setSelectedCurrency(filteredTokens[0]);
    setFormTokenAmount('0');
    setSelectTokenOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, userAddress]);

  const commitAmount = useCallback(
    (value: string) => {
      const parsedValue = Number.parseFloat(value);
      const checkedValue = Number.isNaN(parsedValue)
        ? 0
        : firstDecimals(parsedValue, DECIMALS_INTERNAL);

      setBridgeState((prevState) => ({
        ...prevState,
        token: selectedCurrency,
        amount: checkedValue > 0 ? checkedValue : 0,
        nativeFee: BigInt(0),
        tokenFee: BigInt(0),
      }));
    },
    [selectedCurrency, setBridgeState]
  );

  // Only commit the latest input after the debounce. Comparing against the
  // current field value prevents an older pending value from overwriting MAX.
  useEffect(() => {
    if (debouncedFormTokenAmount !== formTokenAmount) return;

    commitAmount(debouncedFormTokenAmount);
  }, [commitAmount, debouncedFormTokenAmount, formTokenAmount]);

  // check bridge state if user sets max value or bridge max in other components
  // and set amount to show in UI
  useEffect(() => {
    const number: number = firstDecimals(
      bridgeState.amount,
      DEFAULT_BRIDGE_INPUT_DECIMALS
    );
    setFormTokenAmount(number.toString());
  }, [bridgeState.amount]);

  // set amount on user input
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.currentTarget.value.replace(',', '.');

    // 01 -> 1 - remove leading zero
    if (/^0\d/.test(value)) {
      value = parseFloat(value).toString();
    }

    setFormTokenAmount(value || '0');
  };

  const handleAmountFocus = () => {
    setFormTokenAmount((value) =>
      value !== '' && Number(value) === 0 ? '' : value
    );
  };

  const handleAmountBlur = () => {
    setFormTokenAmount((value) => value || '0');
  };

  const onClickMax = () => {
    const maximumBridgeAmount = getMaximumBridgeAmount(
      tokenBalance,
      destinationProxyLiquidity
    );
    const formattedAmount = formatBridgeTokenAmount(
      maximumBridgeAmount,
      bridgeState.token.decimals,
      DEFAULT_BRIDGE_INPUT_DECIMALS
    );

    setFormTokenAmount(formattedAmount);
    commitAmount(formattedAmount);
  };

  const handleTokenChange = (address: `0x${string}`) => {
    const token = filteredTokens.find(
      (item) => item.address[chainId].toLowerCase() === address.toLowerCase()
    );
    if (!token) return;

    setBridgeState((prevState) => ({
      ...prevState,
      token,
      amount: 0,
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
      tokenQuoteLoading: false,
    }));
    setSelectedCurrency(token);
    setSelectTokenOpen(false);
  };

  const tokenBalanceFormatted = formatBridgeBalanceAmount(
    tokenBalance,
    bridgeState.token.decimals,
    DEFAULT_BRIDGE_INPUT_DECIMALS
  );

  return (
    <div className='rounded-none pb-2'>
      <div className='mb-2 flex items-center justify-between'>
        <LabelSmall
          className={cx(twMerge(styles.label, 'text-white-600'))}
          font={'14px'}
        >
          {t('NETWORK')}
        </LabelSmall>
        <LabelSmall
          className={cx(twMerge(styles.label, 'text-white-600'))}
          font={'14px'}
        >
          {t('AMOUNT')}
        </LabelSmall>
      </div>
      <div className='relative'>
        <div className='flex h-[42px] w-full items-stretch rounded-[20px] bg-[#191A1E]'>
          <NetworkSelection allowedNetworks={allowedNetworks} embedded />
          <div className='min-w-0 flex-1'>
            <Input
              onChange={handleAmountChange}
              onFocus={handleAmountFocus}
              onBlur={handleAmountBlur}
              value={formTokenAmount}
              type='text'
              inputMode='decimal'
              inputMinWidth='48px'
              inputTextAlign='right'
              squareLeftCorners
              endEnhancer={
                <div
                  className='flex cursor-pointer flex-row items-center gap-2'
                  onClick={() => setSelectTokenOpen((open) => !open)}
                >
                  <Image
                    src={selectedCurrency.imageUrl ?? ''}
                    className='rounded-full'
                    alt='TokenIcon'
                    width={24}
                    height={24}
                  />
                  <LabelSmall
                    className={cx(
                      twMerge(
                        styles.bridgeFont,
                        styles.responsiveLabel,
                        'text-lg uppercase'
                      )
                    )}
                  >
                    {selectedCurrency.symbol}
                  </LabelSmall>
                  <Image
                    src={getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')}
                    width={16}
                    height={16}
                    alt='arrow-down'
                  />
                </div>
              }
              placeholder={`${t('AMOUNT')} ${selectedCurrency.symbol}`}
              clearOnEscape
            />
          </div>
        </div>
        {selectedAsset && (
          <SelectToken
            open={selectTokenOpen}
            chainId={chainId}
            value={selectedAsset}
            options={assetOptions}
            onClickOutside={() => setSelectTokenOpen(false)}
            onChange={(asset) => handleTokenChange(asset.address)}
          />
        )}
      </div>
      <div className='mt-1 flex items-start justify-end gap-4 text-right text-sm font-medium text-white-400'>
        <span>
          {t('CURRENT_BALANCE')}: {tokenBalanceFormatted}{' '}
          {bridgeState.token.symbol}
        </span>
        <button
          type='button'
          onClick={onClickMax}
          disabled={isDestinationProxyLiquidityLoading}
          className='disabled:cursor-wait disabled:opacity-50'
        >
          {t('MAX')}
        </button>
      </div>
    </div>
  );
}
